import { DefinitionItem, DefinitionList, EmptyState, LoadingState, View } from "components/Shared";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";

/**
 * The assets on ONE amendment — `Assets[]` from the /lease_data payload that
 * named this AmendmentID (GET /v2/assets?amendment_id=:id).
 *
 * An Asset belongs to the AMENDMENT (Sim, 26 Aug): the payload carrying Assets[]
 * is about one AmendmentID, so that is the grain the set is unique at, and this
 * tab is the set at its own grain rather than a roll-up. A sibling term's assets
 * are on that term's page; an amendment whose payload never carried the key has
 * none and shows the empty state.
 *
 * READ-ONLY, and there is nowhere for it to be otherwise: /lease_data is the only
 * writer of an Asset, `V2::AmendmentsController#safe_params` does not permit one,
 * and no endpoint accepts one. The Details tab beside this is editable because
 * those fields are corrections to what Yardi sent; an asset is a record Yardi
 * either sent or did not.
 *
 * NOT `restricted`. The project workspace's Assets tab carries the padlock
 * because it is administrator-only there; this tab is gated exactly as the
 * amendment page around it is (AmendmentPolicy#show?), so a padlock would claim a
 * restriction that is not there. It matches the rest of the lease's tabs, which
 * carry no icons either (Sim, 24 Aug).
 */

interface Props {
	amendmentId: string;
	name?: string;
	icon?: string;
}

// `false` (not "") so DefinitionItem hides the row entirely when there is no
// amount, matching how the other blank fields behave.
const formatAmount = (amount: any): string | false => {
	if (_.isNil(amount) || amount === "") return false;
	const parsed = parseFloat(`${amount}`);
	return _.isNaN(parsed) ? `${amount}` : parsed.toLocaleString();
};

const AmendmentAssetsTab = ({ amendmentId }: Props) => {
	// null = no response yet. Driven from our own state rather than
	// `datasource.isLoading`: with no `renderTrigger` the datasource cannot
	// re-render this component when it flips isLoading back to false, which would
	// leave the tab stuck on <LoadingState />.
	const [assets, setAssets] = useState<any[] | null>(null);
	const [datasource] = useState(new Datasource({ mainModelName: "asset" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setAssets(response.normalizedMainModelResponse || [])
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(amendmentId))
			datasource.get("v2", "assets", {
				queryStringParameters: { amendment_id: amendmentId, per_page: 1000 }
			});
	}, [datasource, amendmentId]);

	if (assets === null) return <LoadingState />;

	// The shared empty state, so an amendment with no assets of its own reads like
	// every other empty list in the app rather than like a stray line of text.
	// Expect it often: only the types mapped to write `Asset` record any, and a
	// payload that says nothing about assets legitimately has none.
	if (_.isEmpty(assets))
		return (
			<View>
				<EmptyState resource="assets" />
			</View>
		);

	return (
		<View>
			{_.map(assets, (asset, index) => (
				<React.Fragment key={asset.id}>
					{index > 0 && <hr style={{ margin: "1.5rem 0" }} />}
					<View title={_.get(asset, "asset_type")}>
						<DefinitionList>
							<DefinitionItem title="asset_type" value={_.get(asset, "asset_type")} />
							<DefinitionItem title="description" value={_.get(asset, "description")} />
							{/* Deliberately NOT the shared <Money> component: it divides by 100
							    because every other money field in the app is stored in cents,
							    whereas assets.amount is a plain numeric holding whole units
							    (Yardi's "AssetAmount": 5000000 means 5,000,000). Thousands
							    separators only. */}
							<DefinitionItem title="amount" value={formatAmount(_.get(asset, "amount"))} />
						</DefinitionList>
					</View>
				</React.Fragment>
			))}
		</View>
	);
};

export default memo(AmendmentAssetsTab);
